import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Truck, Plus, X, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerDeliveryPriority = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('customer_delivery_priority');
  const [viewItem, setViewItem] = useState(null);
  const [deliveryPriorities, setDeliveryPriorities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentPriority, setCurrentPriority] = useState(null);
  const [formData, setFormData] = useState({
    delivery_priority_id: '',
    production_priority: '',
    description: ''
  });

  useEffect(() => {
    loadDeliveryPriorities();
  }, []);

  const loadDeliveryPriorities = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/customer-delivery-priorities`);
      setDeliveryPriorities(response.data);
    } catch (error) {
      console.error('Error loading delivery priorities:', error);
      toast.error('Failed to load delivery priorities');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.delivery_priority_id || !formData.production_priority || !formData.description) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      if (editMode) {
        await axios.put(
          `${BACKEND_URL}/api/customer-delivery-priorities/${currentPriority.delivery_priority_id}`,
          { 
            production_priority: formData.production_priority,
            description: formData.description
          }
        );
        toast.success('Delivery Priority updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/customer-delivery-priorities`, formData);
        toast.success('Delivery Priority created successfully');
      }
      
      setShowDialog(false);
      resetForm();
      loadDeliveryPriorities();
    } catch (error) {
      console.error('Error saving delivery priority:', error);
      toast.error(error.response?.data?.detail || 'Failed to save delivery priority');
    }
  };

  const handleEdit = (priority) => {
    setCurrentPriority(priority);
    setFormData({
      delivery_priority_id: priority.delivery_priority_id,
      production_priority: priority.production_priority,
      description: priority.description
    });
    setEditMode(true);
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({ delivery_priority_id: '', production_priority: '', description: '' });
    setCurrentPriority(null);
    setEditMode(false);
  };

  const handleDialogClose = () => {
    setShowDialog(false);
    resetForm();
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-lime-100 rounded-lg flex items-center justify-center">
                <Truck className="w-6 h-6 text-lime-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Delivery Priorities</h1>
                <p className="text-sm text-gray-600">Define delivery and production priority levels</p>
              </div>
            </div>
            {writable && (<Button
              onClick={() => setShowDialog(true)}
              className="bg-lime-600 hover:bg-lime-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Delivery Priority
            </Button>)}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="w-10 h-10 animate-spin text-lime-600 mb-3" />
              <p className="text-sm text-gray-600">Loading delivery priorities...</p>
            </div>
          ) : deliveryPriorities.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Truck className="w-16 h-16 text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-1">No delivery priorities yet</h3>
              <p className="text-sm text-gray-600 mb-4">Create your first delivery priority</p>
              {writable && (<Button onClick={() => setShowDialog(true)} className="bg-lime-600 hover:bg-lime-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Delivery Priority
              </Button>)}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Delivery Priority</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Production Priority</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {deliveryPriorities.map((priority) => (
                    <tr key={priority.delivery_priority_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-medium text-gray-900">{priority.delivery_priority_id}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 bg-lime-100 text-lime-800 text-xs rounded font-medium">
                          {priority.production_priority}
                        </span>
                      </td>
                      <td className="px-6 py-4">{priority.description}</td>
                      <td className="px-6 py-4 text-right">
                        <RecordActions doctype="customer_delivery_priority" onView={() => setViewItem(priority)} onEdit={() => handleEdit(priority)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Create/Edit Dialog */}
        {showDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editMode ? 'Edit Delivery Priority' : 'Add Delivery Priority'}
                </h2>
                <button
                  onClick={handleDialogClose}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Delivery Priority ID *</Label>
                  <Input
                    maxLength={2}
                    value={formData.delivery_priority_id}
                    onChange={(e) => setFormData({ ...formData, delivery_priority_id: e.target.value.toUpperCase() })}
                    placeholder="e.g., 01, 02"
                    disabled={editMode}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Maximum 2 characters</p>
                </div>

                <div>
                  <Label>Production Priority *</Label>
                  <Input
                    maxLength={2}
                    value={formData.production_priority}
                    onChange={(e) => setFormData({ ...formData, production_priority: e.target.value.toUpperCase() })}
                    placeholder="e.g., 01, 02"
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Maximum 2 characters</p>
                </div>

                <div>
                  <Label>Description *</Label>
                  <Input
                    maxLength={100}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="e.g., High Priority - Next Day Delivery"
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Maximum 100 characters</p>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={handleDialogClose} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-lime-600 hover:bg-lime-700">
                    {editMode ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default CustomerDeliveryPriority;
